import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { PageShell } from "@/components/PageShell";
import { TaskCard } from "@/components/TaskCard";
import { TaskForm } from "@/components/TaskForm";
import { Button } from "@/components/ui/button";
import { Plus, Trash2 } from "lucide-react";

const FILTERS = [
  { key: "active", label: "Active" },
  { key: "done", label: "Done" },
  { key: "skipped", label: "Skipped" },
  { key: "all", label: "All" },
];

export default function AllTasks() {
  const navigate = useNavigate();
  const [tasks, setTasks] = useState([]);
  const [filter, setFilter] = useState("active");
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);

  const load = useCallback(async () => setTasks(await api.listTasks()), []);
  useEffect(() => { load(); }, [load]);

  const visible = tasks.filter((t) => {
    if (filter === "all") return true;
    if (filter === "active") return t.status === "not started" || t.status === "in progress";
    return t.status === filter;
  });

  const submit = async (data) => {
    if (editing) {
      await api.updateTask(editing.id, data);
      toast.success("Task updated.");
    } else {
      await api.createTask(data);
      toast.success("Task added.");
    }
    setOpen(false);
    setEditing(null);
    load();
  };

  const complete = async (t) => { await api.updateTask(t.id, { status: "done" }); toast.success("Done."); load(); };
  const skip = async (t) => { await api.updateTask(t.id, { status: "skipped" }); toast("Skipped."); load(); };
  const remove = async (t) => { await api.deleteTask(t.id); toast("Moved to trash."); load(); };
  const edit = (t) => { setEditing(t); setOpen(true); };

  return (
    <PageShell
      title="All tasks"
      subtitle="Everything in one place."
      action={
        <div className="flex gap-2">
          <Button data-testid="open-trash-button" onClick={() => navigate("/trash")}
            className="rounded-xl bg-zinc-800 hover:bg-zinc-700 text-zinc-400 hover:text-zinc-200 h-11 w-11 p-0">
            <Trash2 className="w-5 h-5" />
          </Button>
          <Button data-testid="add-task-button" onClick={() => { setEditing(null); setOpen(true); }}
            className="rounded-xl bg-[#365458] hover:bg-[#2A4346] h-11 w-11 p-0">
            <Plus className="w-5 h-5" />
          </Button>
        </div>
      }
    >
      <div className="flex gap-2 mb-5 overflow-x-auto no-scrollbar" data-testid="task-filters">
        {FILTERS.map((f) => (
          <button
            key={f.key}
            data-testid={`filter-${f.key}`}
            onClick={() => setFilter(f.key)}
            className={`px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-colors ${
              filter === f.key ? "bg-[#365458] text-white" : "bg-zinc-800 text-zinc-400 hover:text-zinc-200"
            }`}
          >
            {f.label}
          </button>
        ))}
      </div>

      {visible.length === 0 ? (
        <div className="bg-zinc-800/60 rounded-2xl p-6 text-center">
          <p className="text-zinc-400 text-sm">Nothing here. Tap + to add a task.</p>
        </div>
      ) : (
        <div className="space-y-3" data-testid="all-tasks-list">
          {visible.map((t) => (
            <TaskCard
              key={t.id}
              task={t}
              onComplete={complete}
              onSkip={skip}
              onEdit={edit}
              onDelete={remove}
            />
          ))}
        </div>
      )}

      <TaskForm open={open} onOpenChange={setOpen} onSubmit={submit} initial={editing} />
    </PageShell>
  );
}
